/**
 * Free-tier detection for the live channel (FR-LIVE-02, AC-LIVE-005).
 *
 * The live gateway refuses Free-tier tenants with HTTP 402
 * `TIER_LIMIT_EXCEEDED` and, in the same body, names the supported
 * alternative: poll the REST reads every `retryAfter` (5) seconds. A browser
 * cannot read the HTTP status of a failed WebSocket upgrade or `EventSource`,
 * so when the live transports keep failing the client probes the SSE endpoint
 * with a plain `fetch` — which CAN see the 402 — and flips the live hooks into
 * polling mode instead of reconnect-looping forever.
 */
import { toApiError, type ApiError } from './api-error';
import { TIER_LIMIT_ERROR_CODE, TIER_LIMIT_STATUS } from './constants';
import { resolveLiveStreamAuth } from './live-socket';

/** The documented Free-tier cadence; the 402 body's `retryAfter` overrides it. */
export const LIVE_POLL_DEFAULT_INTERVAL_S = 5;
/** While polling, re-check the tier this often so a plan upgrade resumes the socket. */
export const LIVE_TIER_REPROBE_MS = 60_000;
/** Repeated connect failures probe at most this often. */
export const LIVE_TIER_PROBE_THROTTLE_MS = 30_000;

export interface LiveTierProbeResult {
  /** True when the tenant's plan does not include the live channel. */
  isBlocked: boolean;
  /** Poll cadence advertised by the 402 body (`retryAfter`), in seconds. */
  retryAfterSeconds: number;
  /** The 402 as an `ApiError` — feeds the upgrade CTA. `null` unless blocked. */
  error: ApiError | null;
}

export type LiveTierProbe = () => Promise<LiveTierProbeResult>;

export const LIVE_TIER_NOT_BLOCKED: LiveTierProbeResult = {
  isBlocked: false,
  retryAfterSeconds: LIVE_POLL_DEFAULT_INTERVAL_S,
  error: null,
};

/**
 * Probe the SSE branch of `GET /live/v1/stream` for a tier rejection.
 *
 * Every outcome other than a definite 402 `TIER_LIMIT_EXCEEDED` — network
 * failure, 401, 503, or a 200 that starts streaming — reports "not blocked",
 * so the caller keeps its normal reconnect behaviour. A 200 stream is aborted
 * immediately; the probe never holds a connection open.
 */
export function createLiveTierProbe(sseUrl: string, fetchImpl?: typeof fetch): LiveTierProbe {
  return async (): Promise<LiveTierProbeResult> => {
    // Mirrors createLiveSocket/createLiveSseSocket: unit tests inject their
    // own probe; the default must never dial out of jsdom.
    if (import.meta.env?.MODE === 'test' && fetchImpl === undefined) {
      return LIVE_TIER_NOT_BLOCKED;
    }
    const doFetch = fetchImpl ?? globalThis.fetch;
    if (typeof doFetch !== 'function') return LIVE_TIER_NOT_BLOCKED;
    const controller = new AbortController();
    try {
      const auth = resolveLiveStreamAuth();
      const headers: Record<string, string> = { Accept: 'application/json' };
      if (auth.accessToken !== null) headers.Authorization = `Bearer ${auth.accessToken}`;
      if (auth.tenantId !== null) headers['X-Tenant-ID'] = auth.tenantId;
      const response = await doFetch(sseUrl, { headers, signal: controller.signal });
      if (response.status !== TIER_LIMIT_STATUS) {
        controller.abort();
        return LIVE_TIER_NOT_BLOCKED;
      }
      const body: unknown = await response.json().catch(() => null);
      const error = toApiError(
        TIER_LIMIT_STATUS,
        body,
        'Live updates are not included in your current plan.'
      );
      if (error.code !== TIER_LIMIT_ERROR_CODE) return LIVE_TIER_NOT_BLOCKED;
      const record =
        typeof body === 'object' && body !== null ? (body as Record<string, unknown>) : {};
      const retryAfter = record.retryAfter;
      return {
        isBlocked: true,
        retryAfterSeconds:
          typeof retryAfter === 'number' && Number.isFinite(retryAfter) && retryAfter > 0
            ? retryAfter
            : LIVE_POLL_DEFAULT_INTERVAL_S,
        error,
      };
    } catch {
      return LIVE_TIER_NOT_BLOCKED;
    }
  };
}
